import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/components/ui/button';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from '@/icons/Icon';
import {
  ALERT_BANNER_ACTION_CLASS,
  ALERT_BANNER_SUBTLE_ACTION_CLASS,
} from './alert-banner-helpers';

/**
 * Inline status / feedback banner. Consolidates the hand-rolled `role="alert"`
 * boxes that were re-implemented across auth, settings, and dialogs (see
 * `features/auth/FormFeedback.tsx`, `settings/common.tsx` RetryBlock, the
 * invite/demote dialogs). Uses semantic colour tokens so it tracks the palette.
 *
 * Two appearances. `soft` (default, every banner inside a page): a tone tint
 * with a leading tone icon, body text in ink (not in the tone colour — amber
 * text on an amber tint never reached 4.5:1), and an optional bold `title` in a
 * darker shade of the tone. `solid` (the app-wide billing / usage banners above
 * every page): a full tone fill with light text and a white CTA. The root sets
 * `--banner-*` custom properties per tone and appearance, which the icon, the
 * title, the dismiss and `AlertBannerAction` read — so one set of child classes
 * covers every tone, both appearances and both themes.
 *
 * a11y: the `error` variant is an assertive `role="alert"` (announced
 * immediately); the others are polite `role="status"`.
 *
 * `action` is the slot for a recovery control (typically `AlertBannerAction`)
 * that the message needs but a toast can't carry. It sits outside the text and
 * before the dismiss button, and wraps below the message on a narrow screen
 * rather than squashing it.
 */
const alertBannerVariants = cva(
  'flex flex-wrap items-center gap-x-3 gap-y-2 min-h-11 rounded-lg border text-sm leading-5',
  {
    variants: {
      // Each tone defines its colours as custom properties; `appearance` decides
      // which of them paint the surface. Solid warning fills with `--gold`, not
      // `--warning`: white on the amber warning fill is ~2.3:1.
      variant: {
        error:
          '[--banner-tone:hsl(var(--destructive))] [--banner-soft-ink:hsl(var(--destructive))] [--banner-solid:hsl(var(--destructive))] [--banner-on-solid:hsl(var(--destructive-foreground))]',
        success:
          // Solid fills with `--good-600`, not `--success`: white on the
          // brighter success green is ~3.3:1.
          '[--banner-tone:hsl(var(--success))] [--banner-soft-ink:var(--good-600)] [--banner-solid:var(--good-600)] [--banner-on-solid:hsl(var(--success-foreground))]',
        warning:
          '[--banner-tone:var(--warn-600)] [--banner-soft-ink:color-mix(in_srgb,var(--warn-600)_80%,var(--ink-900))] [--banner-solid:hsl(var(--gold))] [--banner-on-solid:hsl(var(--gold-foreground))]',
        info: '[--banner-tone:hsl(var(--info))] [--banner-soft-ink:hsl(var(--info))] [--banner-solid:hsl(var(--info))] [--banner-on-solid:hsl(var(--info-foreground))]',
      },
      // `soft` — tinted surface, ink text: inline banners inside a page.
      // `solid` — full tone fill, light text: the app-wide banners above every
      // page, which must read as louder than anything in the page below them.
      appearance: {
        soft: 'text-[var(--ink-800)] [--banner-icon:var(--banner-tone)] [--banner-ink:var(--banner-soft-ink)] [--banner-action-ink:var(--banner-soft-ink)] [--banner-subtle-line:transparent] [--banner-dismiss:var(--ink-500)]',
        solid:
          'border-transparent bg-[var(--banner-solid)] text-[var(--banner-on-solid)] [--banner-icon:currentColor] [--banner-ink:currentColor] [--banner-action-ink:var(--banner-solid)] [--banner-line:transparent] [--banner-hover:hsl(var(--card)/0.88)] [--banner-ghost-hover:hsl(var(--card)/0.15)] [--banner-subtle-line:currentColor] [--banner-dismiss:currentColor]',
      },
    },
    compoundVariants: [
      {
        appearance: 'soft',
        variant: 'error',
        class:
          'border-destructive/25 bg-destructive/[0.06] [--banner-line:hsl(var(--destructive)/0.3)] [--banner-hover:hsl(var(--destructive)/0.08)] [--banner-ghost-hover:hsl(var(--destructive)/0.08)]',
      },
      {
        appearance: 'soft',
        variant: 'success',
        class:
          'border-success/25 bg-success/[0.06] [--banner-line:hsl(var(--success)/0.3)] [--banner-hover:hsl(var(--success)/0.08)] [--banner-ghost-hover:hsl(var(--success)/0.08)]',
      },
      {
        appearance: 'soft',
        variant: 'warning',
        class:
          'border-warning/30 bg-warning/[0.08] [--banner-line:hsl(var(--warning)/0.4)] [--banner-hover:hsl(var(--warning)/0.12)] [--banner-ghost-hover:hsl(var(--warning)/0.12)]',
      },
      {
        appearance: 'soft',
        variant: 'info',
        class:
          'border-info/25 bg-info/[0.06] [--banner-line:hsl(var(--info)/0.3)] [--banner-hover:hsl(var(--info)/0.08)] [--banner-ghost-hover:hsl(var(--info)/0.08)]',
      },
    ],
    defaultVariants: {
      variant: 'error',
      appearance: 'soft',
    },
  },
);

type Variant = NonNullable<VariantProps<typeof alertBannerVariants>['variant']>;
type BannerIcon = React.ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean }>;

// Kept unexported so this module exports only React components and stays a Vite
// Fast Refresh boundary.
const ROLE_BY_VARIANT = {
  error: 'alert',
  success: 'status',
  warning: 'status',
  info: 'status',
} as const;

const ICON_BY_VARIANT: Record<Variant, BannerIcon> = {
  error: AlertCircle,
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
};

type Appearance = NonNullable<VariantProps<typeof alertBannerVariants>['appearance']>;

export interface AlertBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role' | 'title'> {
  /** Tone. Defaults to `error`. */
  variant?: Variant;
  /**
   * `soft` (default) for every banner inside a page; `solid` only for the
   * app-wide billing / usage banners above every page.
   */
  appearance?: Appearance;
  /**
   * Bold lead rendered before `children`, in the tone's ink — e.g. the first
   * sentence of the message. Optional; a banner without one reads as plain text.
   */
  title?: React.ReactNode;
  /**
   * Put the `title` on its own line above `children` instead of leading the
   * sentence. Use it for a title that is a label rather than a sentence
   * ("Team invites", "You have view-only access"), which would otherwise run
   * straight into the body.
   */
  stackTitle?: boolean;
  /**
   * The leading glyph. Defaults to the variant's icon; pass another icon
   * component to be specific (a card for a payment failure), or `null` for none.
   */
  icon?: BannerIcon | null;
  /**
   * Optional recovery control rendered next to (before) the dismiss button —
   * typically `<AlertBannerAction>Retry</AlertBannerAction>`. Renders outside the
   * message so it never inherits the text's wrapping.
   */
  action?: React.ReactNode;
  /** When provided, renders a trailing dismiss button. */
  onDismiss?: () => void;
  /** Accessible label for the dismiss button. Defaults to "Dismiss". */
  dismissLabel?: string;
}

export function AlertBanner({
  variant = 'error',
  appearance = 'soft',
  title,
  stackTitle = false,
  icon,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
  children,
  ...props
}: AlertBannerProps) {
  const role = ROLE_BY_VARIANT[variant];
  const Icon = icon === undefined ? ICON_BY_VARIANT[variant] : icon;
  const hasTrailing = Boolean(action || onDismiss);
  return (
    <div
      role={role}
      aria-live={role === 'status' ? 'polite' : undefined}
      data-variant={variant}
      data-appearance={appearance}
      className={cn(
        alertBannerVariants({ variant, appearance }),
        // A trailing 32px button fills the 44px row, so the padding tightens
        // around it; a text-only banner keeps even padding on both sides.
        hasTrailing ? 'py-1.5 pl-3.5 pr-2' : 'px-3.5 py-2.5',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 basis-64 items-start gap-2.5">
        {Icon && (
          <Icon size={18} aria-hidden className="mt-px shrink-0 text-[var(--banner-icon)]" />
        )}
        <div className="min-w-0 flex-1">
          {title &&
            (stackTitle ? (
              <strong className="block font-extrabold text-[var(--banner-ink)]">{title}</strong>
            ) : (
              <>
                <strong className="font-extrabold text-[var(--banner-ink)]">{title}</strong>{' '}
              </>
            ))}
          {children}
        </div>
      </div>
      {hasTrailing && (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {action}
          {onDismiss && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={dismissLabel}
              onClick={onDismiss}
              className="h-8 w-8 shrink-0 p-0 text-[var(--banner-dismiss)] hover:bg-[var(--banner-ghost-hover)] hover:text-[var(--banner-ink)]"
            >
              <X size={16} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export interface AlertBannerActionProps extends Omit<ButtonProps, 'variant' | 'size'> {
  /** `true` for a second, lower-priority CTA: no outline, tone text only. */
  subtle?: boolean;
}

/**
 * The CTA for an `AlertBanner`'s `action` slot: a white button outlined and
 * lettered in the banner's tone (`subtle` drops the outline). Must render inside
 * an `AlertBanner` — it reads the tone from the banner's custom properties.
 */
export const AlertBannerAction = React.forwardRef<HTMLButtonElement, AlertBannerActionProps>(
  ({ subtle = false, className, type = 'button', ...props }, ref) => (
    <Button
      ref={ref}
      type={type}
      size="sm"
      variant="outline"
      className={cn(subtle ? ALERT_BANNER_SUBTLE_ACTION_CLASS : ALERT_BANNER_ACTION_CLASS, className)}
      {...props}
    />
  ),
);
AlertBannerAction.displayName = 'AlertBannerAction';
